import { Box } from "@material-ui/core";
import clsx from "clsx";
import React from "react";
import { useHistory } from "react-router";
import {
  CLICKED_CURATED_HOTEL,
  DatelessSearchSource,
  PATH_PREMIER_COLLECTION_SHOP_DETAILS,
  VibePrimaryLodging,
  VIEWED_CURATED_HOTEL,
  transformToShopDetailsStringifiedQuery,
} from "redmond";
import { StayType } from "redmond/hotels-module/interfaces";
import {
  ActionLink,
  CloseButtonIcon,
  DesktopPopupModal,
  MosaicCarouselPage,
  MobilePopoverCard,
  PagedContentCarousel,
} from "halifax";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { WishlistToggleHeart } from "../../../wishlist";
import styles from "./styles.module.scss";

export interface PrimaryPropertiesSectionProps {
  properties?: VibePrimaryLodging[];
  vibeName: string;
  isMobile: boolean;
  isWishlistExperiment: boolean;
}

export const PrimaryPropertiesSection: React.FC<PrimaryPropertiesSectionProps> = ({
  properties = [],
  vibeName,
  isMobile,
  isWishlistExperiment,
}) => {
  const history = useHistory();
  const [visibleIndex, setVisibleIndex] = React.useState(0);
  const [expandedImage, setExpandedImage] = React.useState<{
    url: string;
    alt: string;
  } | null>(null);

  React.useEffect(() => {
    const property = properties[visibleIndex];
    if (property && vibeName) {
      trackEvent({
        eventName: VIEWED_CURATED_HOTEL,
        properties: {
          hotel_name: property.name,
          lodging_id: property.lodgingId,
          hotel_index: visibleIndex,
          section: "primary",
          vibe_name: vibeName,
        },
      });
    }
  }, [visibleIndex, properties, vibeName]);

  const handleViewDetails = React.useCallback(
    (lodgingId: string, propertyName: string) => {
      trackEvent({
        eventName: CLICKED_CURATED_HOTEL,
        properties: {
          source: "vibes_primary",
          vibe_name: vibeName,
          hotel_id: lodgingId,
          hotel_name: propertyName,
        },
      });
      const search = transformToShopDetailsStringifiedQuery({
        lodgingId,
        source: DatelessSearchSource.Vibes,
        vibe: vibeName,
      });
      const url = history.createHref({
        pathname: PATH_PREMIER_COLLECTION_SHOP_DETAILS,
        search,
      });
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [history, vibeName]
  );

  const closeExpandedImage = React.useCallback(() => setExpandedImage(null), []);

  const expandedImageContent =
    expandedImage !== null ? (
      <Box className={styles.lightboxWrapper}>
        <img
          src={expandedImage.url}
          alt={expandedImage.alt}
          className={styles.lightboxImg}
        />
      </Box>
    ) : null;

  const mobileExpandedImagePopover =
    expandedImage !== null ? (
      <MobilePopoverCard
        className={styles.lightboxMobileRoot}
        open
        onClose={closeExpandedImage}
        fullScreen
        contentClassName={styles.lightboxContentMobile}
        topRightButton={
          <ActionLink
            className={styles.lightboxClose}
            content={<CloseButtonIcon />}
            label="Close"
            onClick={closeExpandedImage}
          />
        }
      >
        {expandedImageContent}
      </MobilePopoverCard>
    ) : null;

  const desktopExpandedImagePopover =
    expandedImage !== null ? (
      <DesktopPopupModal
        open
        onClose={closeExpandedImage}
        invisibleBackdrop={false}
        contentClassName={styles.lightboxContent}
      >
        {expandedImageContent}
      </DesktopPopupModal>
    ) : null;

  const pages = properties.map((property) => {
    const orderedImages = [...(property.images?.images ?? [])].sort(
      (left, right) => left.priority - right.priority
    );
    const imageSources = orderedImages.map((image) => image.source);
    const primaryImage = imageSources[0];

    return (
      <Box className={styles.pageWrapper} key={property.lodgingId}>
        {isWishlistExperiment && (
          <WishlistToggleHeart
            listingId={property.lodgingId}
            listingImageUrl={primaryImage}
            stayType={StayType.Hotels}
            collection={property.lodgingCollection}
            style={{
              position: "absolute",
              top: isMobile ? 10 : 24,
              right: isMobile ? 10 : 24,
              zIndex: 1,
            }}
          />
        )}
        <MosaicCarouselPage
          titlePrefix="Our take on"
          title={property.name}
          eyebrow={`${property.location} • ${property.lodgingCollection} Collection`}
          body={property.description}
          cta="View details"
          onCtaClick={() => handleViewDetails(property.lodgingId, property.name)}
          onImageClick={(imageUrl: string) =>
            setExpandedImage({
              url: imageUrl,
              alt: `${property.name} expanded view`,
            })
          }
          images={imageSources}
        />
      </Box>
    );
  });

  return (
    <Box
      className={clsx(styles.root, {
        [styles.mobile]: isMobile,
      })}
    >
      <PagedContentCarousel
        pages={pages}
        isMobile={isMobile}
        className={styles.carousel}
        onIndexChange={setVisibleIndex}
      />
      {isMobile ? mobileExpandedImagePopover : desktopExpandedImagePopover}
    </Box>
  );
};
